import React from "react";
import CustomDropdown from "..";
import { useCurrency } from "../../../contexts/currency.context";

/**
 * Currency Dropdown Component (extends CustomDropdown)
 * 
 * @param {Object} props
 * @param {string} props.value - Selected currency code (3-letter ISO code) or empty string
 * @param {function} props.onChange - Callback function when selection changes
 * @param {string} props.label - Label for the dropdown (default: "Currency")
 * @param {string} props.id - HTML id attribute
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {string} props.size - Size of the input ("small", "medium")
 * @param {string} props.variant - Variant of the input ("outlined", "filled", "standard")
 * @param {Object} props.sx - Additional styling
 * @param {string} props.helperText - Helper text below the field
 * @param {boolean} props.error - Whether to show error state
 * @returns {JSX.Element}
 */
const CurrencyDropdown = ({
  value = "",
  onChange,
  label = "Currency",
  id = "currency",
  required = false,
  disabled = false,
  size = "small",
  variant = "outlined",
  sx = {},
  helperText = "",
  error = false,
  ...otherProps
}) => {
  const { currencies: CURRENCIES } = useCurrency();

  // Filter currencies based on search term
  const filterCurrencies = (currencies, searchTerm) => {
    return currencies.filter(currency => 
      currency?.currencyName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      currency?.currencyCode?.toLowerCase()?.includes(searchTerm.toLowerCase())
    );
  };

  // Get display label for currency
  const getCurrencyLabel = (currency) => {
    return `${currency?.currencyCode} - ${currency?.currencyName}`;
  };

  // Get value from currency object
  const getCurrencyValue = (currency) => {
    return currency?.currencyCode;
  };

  return (
    <CustomDropdown
      value={value}
      onChange={onChange}
      items={CURRENCIES}
      getItemLabel={getCurrencyLabel}
      getItemValue={getCurrencyValue}
      filterItems={filterCurrencies}
      label={label}
      id={id}
      required={required}
      disabled={disabled}
      size={size}
      variant={variant}
      sx={sx}
      helperText={helperText}
      error={error}
      placeholder="Search currencies..."
      showClearOption={true}
      clearOptionLabel="Clear selection"
      noResultsLabel="No currencies found"
      autoWidth={true}
      {...otherProps}
    />
  );
};

export default CurrencyDropdown;